import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AdminAuthGate } from './components/admin/AdminAuthGate';
import { AdminApp } from './components/admin/AdminApp';
import { ApiAuthError } from './lib/api';
import { adminLogout } from './lib/adminAuth';
import './index.css';

// ── React Query ──────────────────────────────────────────────────────
// On a 401/403 from any admin-svc query we clear the staff session, which
// flips AdminAuthGate back to the login screen (the stored JWT is dead).
// Other errors surface per-page.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiAuthError) return false;
        return failureCount < 2;
      },
      staleTime: 1000,
    },
  },
});

queryClient.getQueryCache().subscribe((event) => {
  const err = event.query.state.error;
  if (err instanceof ApiAuthError) {
    // 401 ONLY — the staff token expired, so drop the session and let the gate
    // bounce to login. A 403 (ApiForbiddenError) is deliberately NOT handled
    // here: the role simply can't reach that resource, and destroying a valid
    // session over it is what made a narrow role look like a broken login.
    adminLogout();
  }
});

// App root: QueryClientProvider → BrowserRouter → AdminAuthGate → AdminApp.
// The Admin pages route at the ROOT ('/customers', '/drivers', …) — this is a
// dedicated single-purpose app, so there is no '/admin' prefix.
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <AdminAuthGate>
          <AdminApp />
        </AdminAuthGate>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
